import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2, Package, Camera, Sparkles, Mic, Square, Upload } from "lucide-react";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Redimensiona/comprime e converte para JPEG (resolve HEIC do iPhone e payloads grandes no celular)
const compressImage = (file, maxDim = 1280, quality = 0.7) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    try {
      let { width, height } = img;
      if (!width || !height) return reject(new Error("Imagem inválida"));
      if (Math.max(width, height) > maxDim) {
        const s = maxDim / Math.max(width, height);
        width = Math.round(width * s);
        height = Math.round(height * s);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas indisponível"));
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      canvas.width = 0; canvas.height = 0;
      resolve(dataUrl);
    } catch (e) { reject(e); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Não foi possível abrir a imagem")); };
  img.src = url;
});

const emptyCliente = { nome: "", cpf: "", telefone: "", whatsapp: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", cep: "", ponto_referencia: "" };

export default function NovaFicha() {
  const { id } = useParams();
  const editing = !!id;
  const [searchParams] = useSearchParams();
  const prefillNome = searchParams.get("cliente");
  const prefillKey = searchParams.get("key");
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(emptyCliente);
  const [folha, setFolha] = useState("");
  const [vendedor, setVendedor] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [produtos, setProdutos] = useState([{ descricao: "", quantidade: 1, valor_unitario: "" }]);
  const [desconto, setDesconto] = useState("");
  const [acrescimo, setAcrescimo] = useState("");
  const [entrada, setEntrada] = useState("");
  const [forma, setForma] = useState("dinheiro");
  const [valorParcela, setValorParcela] = useState("");
  const [periodicidade, setPeriodicidade] = useState("mensal");
  const [primeiro, setPrimeiro] = useState("");
  const [saving, setSaving] = useState(false);
  const [extraindo, setExtraindo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [transcrevendo, setTranscrevendo] = useState(false);
  const [recorder, setRecorder] = useState(null);

  useEffect(() => {
    if (editing) return;
    if (!prefillNome && !prefillKey) return;
    const params = prefillKey ? { key: prefillKey } : { nome: prefillNome };
    api.get(`/cliente`, { params })
      .then(({ data }) => setCliente({ ...emptyCliente, ...data.cliente }))
      .catch(() => {});
  }, [prefillNome, prefillKey, editing]);

  useEffect(() => {
    if (!editing) return;
    api.get(`/fichas/${id}`).then(({ data }) => {
      setCliente({ ...emptyCliente, ...data.cliente });
      setFolha(data.folha || ""); setVendedor(data.vendedor || ""); setObservacoes(data.observacoes || "");
      setProdutos(data.produtos.length ? data.produtos.map((p) => ({ descricao: p.descricao, quantidade: p.quantidade, valor_unitario: p.valor_unitario })) : [{ descricao: "", quantidade: 1, valor_unitario: "" }]);
      setDesconto(data.desconto || ""); setAcrescimo(data.acrescimo || ""); setEntrada(data.entrada || "");
      setForma(data.forma_pagamento || "dinheiro"); setValorParcela(data.valor_parcela || "");
      setPeriodicidade(data.periodicidade || "mensal"); setPrimeiro(data.primeiro_vencimento || "");
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [id, editing]);

  const aplicarExtracao = (data) => {
    const c = data.cliente || {};
    setCliente((prev) => ({
      ...prev,
      nome: c.nome || prev.nome,
      telefone: c.telefone || prev.telefone,
      endereco: c.endereco || prev.endereco,
      bairro: c.bairro || prev.bairro,
      cidade: c.cidade || prev.cidade,
      estado: c.estado || prev.estado,
    }));
    if (Array.isArray(data.produtos) && data.produtos.length) {
      setProdutos(data.produtos.map((p) => ({
        descricao: p.descricao || "",
        quantidade: p.quantidade || 1,
        valor_unitario: p.valor_unitario || "",
      })));
    }
    if (data.vendedor) setVendedor(String(data.vendedor));
    if (data.entrada) setEntrada(String(data.entrada));
    if (data.valor_parcela) setValorParcela(String(data.valor_parcela));
    if (data.primeiro_vencimento) setPrimeiro(data.primeiro_vencimento);
    if (data.periodicidade) setPeriodicidade(data.periodicidade);
  };

  const handleFoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!(file.type || "").startsWith("image/")) return toast.error("Selecione uma imagem");
    setExtraindo(true);
    try {
      let b64;
      try { b64 = await compressImage(file); }
      catch { b64 = await fileToBase64(file); }
      const { data } = await api.post("/fichas/extrair-foto", { image_base64: b64 });
      aplicarExtracao(data);
      toast.success("Dados extraídos! Confira e ajuste antes de salvar.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Não foi possível ler a foto");
    } finally {
      setExtraindo(false);
    }
  };

  const iniciarGravacao = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return toast.error("Seu navegador não permite gravar áudio");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (typeof MediaRecorder === "undefined") {
        stream.getTracks().forEach((t) => t.stop());
        return toast.error("Seu navegador não suporta gravação de áudio");
      }
      let mimeType = "";
      for (const t of ["audio/webm", "audio/mp4", "audio/mpeg", "audio/ogg"]) {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) { mimeType = t; break; }
      }
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunks.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        setTranscrevendo(true);
        try {
          const b64 = await fileToBase64(blob);
          const { data } = await api.post("/fichas/extrair-audio", { audio_base64: b64 });
          aplicarExtracao(data);
          toast.success("Áudio interpretado! Confira e ajuste antes de salvar.");
        } catch (err) {
          toast.error(apiError(err.response?.data?.detail) || "Não foi possível interpretar o áudio");
        } finally {
          setTranscrevendo(false);
        }
      };
      mr.start();
      setRecorder(mr);
      setGravando(true);
    } catch (err) {
      toast.error("Não foi possível acessar o microfone");
    }
  };

  const pararGravacao = () => {
    if (recorder && recorder.state !== "inactive") recorder.stop();
    setGravando(false);
    setRecorder(null);
  };

  const setC = (k) => (e) => setCliente({ ...cliente, [k]: e.target.value });
  const setProd = (i, k, v) => { const n = [...produtos]; n[i][k] = v; setProdutos(n); };
  const addProd = () => setProdutos([...produtos, { descricao: "", quantidade: 1, valor_unitario: "" }]);
  const rmProd = (i) => setProdutos(produtos.filter((_, x) => x !== i));

  const subtotal = useMemo(() => produtos.reduce((s, p) => s + (parseFloat(p.quantidade) || 0) * (parseFloat(p.valor_unitario) || 0), 0), [produtos]);
  const total = useMemo(() => subtotal - (parseFloat(desconto) || 0) + (parseFloat(acrescimo) || 0), [subtotal, desconto, acrescimo]);
  const aFinanciar = Math.max(total - (parseFloat(entrada) || 0), 0);
  const preview = useMemo(() => {
    const a = Math.round(aFinanciar * 100);
    const p = Math.round((parseFloat(valorParcela) || 0) * 100);
    if (a <= 0 || p <= 0) return { qtd: 0, valor: 0, ultima: 0 };
    const full = Math.floor(a / p);
    const rem = a - full * p;
    const qtd = full + (rem > 0 ? 1 : 0);
    const valor = full > 0 ? p / 100 : rem / 100;
    const ultima = rem > 0 ? rem / 100 : p / 100;
    return { qtd, valor, ultima };
  }, [aFinanciar, valorParcela]);

  const submit = async (e) => {
    e.preventDefault();
    if (!cliente.nome) return toast.error("Informe o nome do cliente");
    if (!primeiro) return toast.error("Informe a data do primeiro vencimento");
    if (!editing && aFinanciar > 0 && !(parseFloat(valorParcela) > 0)) return toast.error("Informe o valor da parcela");
    setSaving(true);
    const payload = {
      folha, cliente, vendedor, observacoes,
      produtos: produtos.map((p) => ({ descricao: p.descricao, quantidade: parseFloat(p.quantidade) || 0, valor_unitario: parseFloat(p.valor_unitario) || 0 })),
      desconto: parseFloat(desconto) || 0, acrescimo: parseFloat(acrescimo) || 0, entrada: parseFloat(entrada) || 0,
      forma_pagamento: forma, valor_parcela: parseFloat(valorParcela) || 0, primeiro_vencimento: primeiro, periodicidade,
    };
    try {
      if (editing) { await api.put(`/fichas/${id}`, payload); toast.success("Ficha atualizada!"); navigate(`/painel/ficha/${id}`); }
      else { const { data } = await api.post("/fichas", payload); toast.success("Ficha criada!"); navigate(`/painel/ficha/${data.id}`); }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const field = (label, k, ph = "", cls = "") => (
    <div className={cls}><Label className="text-muted-foreground text-sm">{label}</Label>
      <Input value={cliente[k]} onChange={setC(k)} placeholder={ph} data-testid={`cliente-${k}`} className="mt-1 bg-card" style={{ height: 46 }} /></div>
  );

  return (
    <AdminLayout>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"><ArrowLeft className="w-4 h-4" /> Voltar</button>
      <h1 className="font-display text-3xl font-black text-foreground mb-6">{editing ? "Editar ficha" : "Nova ficha"}</h1>

      {!editing && (
        <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/10 p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-primary/20 p-2.5"><Sparkles className="w-5 h-5 text-primary" /></div>
            <div className="flex-1">
              <h2 className="font-display text-base font-bold text-foreground">Preencher automaticamente com IA</h2>
              <p className="text-muted-foreground text-sm mt-0.5">Tire uma foto da ficha de papel <span className="text-foreground/70">ou</span> fale a venda em voz alta — a IA preenche os campos para você.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <input id="foto-camera-input" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handleFoto} className="hidden" data-testid="foto-camera-input" disabled={extraindo || gravando || transcrevendo} />
                <input id="foto-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFoto} className="hidden" data-testid="foto-ficha-input" disabled={extraindo || gravando || transcrevendo} />
                <Button type="button" disabled={extraindo || gravando || transcrevendo} onClick={() => document.getElementById("foto-camera-input")?.click()} className="rounded-full font-semibold" data-testid="foto-ficha-btn">
                  {extraindo ? <><Loader2 className="w-4 h-4 animate-spin" /> Analisando foto...</> : <><Camera className="w-4 h-4" /> Tirar foto</>}
                </Button>
                <Button type="button" variant="outline" disabled={extraindo || gravando || transcrevendo} onClick={() => document.getElementById("foto-file-input")?.click()} className="rounded-full font-semibold" data-testid="foto-arquivo-btn">
                  <Upload className="w-4 h-4" /> Enviar arquivo
                </Button>
                {!gravando ? (
                  <Button type="button" variant="outline" disabled={extraindo || transcrevendo} onClick={iniciarGravacao} className="rounded-full font-semibold" data-testid="audio-record-btn">
                    {transcrevendo ? <><Loader2 className="w-4 h-4 animate-spin" /> Interpretando áudio...</> : <><Mic className="w-4 h-4" /> Falar a venda</>}
                  </Button>
                ) : (
                  <Button type="button" onClick={pararGravacao} className="rounded-full font-semibold bg-red-500 hover:bg-red-600 text-white animate-pulse" data-testid="audio-stop-btn">
                    <Square className="w-4 h-4" /> Parar e transcrever
                  </Button>
                )}
              </div>
              {gravando && <p className="text-xs text-red-400 mt-2 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block animate-pulse" /> Gravando... fale o nome do cliente, produtos, valores, entrada e vencimento.</p>}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        <Section title="Dados do cliente">
          <div className="grid sm:grid-cols-2 gap-3">
            {field("Nome completo *", "nome")}
            {field("Telefone", "telefone", "+55 11 99999-9999")}
            {field("WhatsApp", "whatsapp", "+55 11 99999-9999")}
            <div><Label className="text-muted-foreground text-sm">Vendedor</Label><Input value={vendedor} onChange={(e) => setVendedor(e.target.value)} data-testid="cliente-vendedor" className="mt-1 bg-card" style={{ height: 46 }} /></div>
            {field("Endereço", "endereco", "", "sm:col-span-2")}
            {field("Número", "numero")}
            {field("Bairro", "bairro")}
          </div>
        </Section>

        <Section title="Ficha">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-muted-foreground text-sm">Número da folha</Label><Input value={folha} onChange={(e) => setFolha(e.target.value)} data-testid="ficha-folha" className="mt-1 bg-card" style={{ height: 46 }} /></div>
            <div className="sm:col-span-2"><Label className="text-muted-foreground text-sm">Observações do cliente</Label><Textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} data-testid="ficha-obs" className="mt-1 bg-card" /></div>
          </div>
        </Section>

        <Section title="Produtos / objetos vendidos">
          <div className="space-y-3">
            {produtos.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 sm:col-span-6"><Label className="text-muted-foreground text-xs">Descrição</Label><Input value={p.descricao} onChange={(e) => setProd(i, "descricao", e.target.value)} data-testid={`produto-desc-${i}`} className="mt-1 bg-card" style={{ height: 44 }} /></div>
                <div className="col-span-4 sm:col-span-2"><Label className="text-muted-foreground text-xs">Qtd</Label><Input type="number" step="0.01" value={p.quantidade} onChange={(e) => setProd(i, "quantidade", e.target.value)} data-testid={`produto-qtd-${i}`} className="mt-1 bg-card" style={{ height: 44 }} /></div>
                <div className="col-span-5 sm:col-span-3"><Label className="text-muted-foreground text-xs">Vlr unit.</Label><Input type="number" step="0.01" value={p.valor_unitario} onChange={(e) => setProd(i, "valor_unitario", e.target.value)} data-testid={`produto-vu-${i}`} className="mt-1 bg-card" style={{ height: 44 }} placeholder="0,00" /></div>
                <div className="col-span-3 sm:col-span-1 flex justify-end">
                  {produtos.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => rmProd(i)} className="text-red-400" data-testid={`produto-rm-${i}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addProd} className="rounded-full" data-testid="add-produto"><Plus className="w-4 h-4" /> Adicionar produto</Button>
          </div>
        </Section>

        <Section title="Valores">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label className="text-muted-foreground text-sm">Desconto</Label><Input type="number" step="0.01" value={desconto} onChange={(e) => setDesconto(e.target.value)} data-testid="valor-desconto" className="mt-1 bg-card" style={{ height: 46 }} placeholder="0,00" /></div>
            <div><Label className="text-muted-foreground text-sm">Acréscimo</Label><Input type="number" step="0.01" value={acrescimo} onChange={(e) => setAcrescimo(e.target.value)} data-testid="valor-acrescimo" className="mt-1 bg-card" style={{ height: 46 }} placeholder="0,00" /></div>
            <div><Label className="text-muted-foreground text-sm">Entrada</Label><Input type="number" step="0.01" value={entrada} onChange={(e) => setEntrada(e.target.value)} data-testid="valor-entrada" className="mt-1 bg-card" style={{ height: 46 }} placeholder="0,00" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
            <Totitem label="Subtotal" value={brl(subtotal)} />
            <Totitem label="Total da compra" value={brl(total)} strong />
            <Totitem label="Entrada" value={brl(parseFloat(entrada) || 0)} />
            <Totitem label="A financiar" value={brl(aFinanciar)} accent />
          </div>
        </Section>

        {!editing && (
          <Section title="Configuração da prestação">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-muted-foreground text-sm">Forma de pagamento</Label>
                <Select value={forma} onValueChange={setForma}><SelectTrigger data-testid="forma-select" className="mt-1 bg-card" style={{ height: 46 }}><SelectValue /></SelectTrigger>
                  <SelectContent>{["dinheiro", "pix", "cartao", "transferencia", "outro"].map((o) => <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-muted-foreground text-sm">Periodicidade</Label>
                <Select value={periodicidade} onValueChange={setPeriodicidade}><SelectTrigger data-testid="period-select" className="mt-1 bg-card" style={{ height: 46 }}><SelectValue /></SelectTrigger>
                  <SelectContent>{["semanal", "quinzenal", "mensal"].map((o) => <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-muted-foreground text-sm">Valor da parcela</Label><Input type="number" step="0.01" min="0" value={valorParcela} onChange={(e) => setValorParcela(e.target.value)} data-testid="valor-parcela" className="mt-1 bg-card" style={{ height: 46 }} placeholder="0,00" /></div>
              <div><Label className="text-muted-foreground text-sm">1º vencimento *</Label><Input type="date" value={primeiro} onChange={(e) => setPrimeiro(e.target.value)} data-testid="primeiro-venc" className="mt-1 bg-card" style={{ height: 46 }} /></div>
            </div>
            <div className="mt-3 rounded-xl bg-primary/10 border border-primary/30 p-4 flex items-center justify-between" data-testid="parcelas-preview">
              <span className="text-muted-foreground text-sm flex items-center gap-2"><Package className="w-4 h-4" /> {preview.qtd}x de</span>
              <div className="text-right">
                <span className="font-display text-2xl font-black text-primary">{brl(preview.valor)}</span>
                {preview.qtd > 1 && Math.abs(preview.ultima - preview.valor) > 0.005 && (
                  <p className="text-xs text-muted-foreground">última: {brl(preview.ultima)}</p>
                )}
              </div>
            </div>
          </Section>
        )}

        <Button type="submit" disabled={saving} data-testid="salvar-ficha" className="w-full rounded-full font-semibold text-lg" style={{ height: 56 }}>
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : editing ? "Salvar alterações" : "Criar ficha e gerar parcelas"}
        </Button>
      </form>
    </AdminLayout>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <h2 className="font-display text-lg font-bold text-primary mb-4">{title}</h2>
    {children}
  </div>
);

const Totitem = ({ label, value, strong, accent }) => (
  <div className="rounded-xl bg-white/5 p-3">
    <p className="text-muted-foreground text-xs">{label}</p>
    <p className={`font-display font-bold ${accent ? "text-primary" : strong ? "text-foreground text-lg" : "text-foreground"}`}>{value}</p>
  </div>
);
